import { Element } from "@xmpp/xml"
import { Settings, PlannerCompany } from "../reducers/settingsSlice"
import {
  type PushToken,
  type CarRouteConfig,
  type AvoidType,
  type TravelMode,
  type LoadType,
  EmissionType,
  type RouteAlertConfig,
} from "../api/adHocCommands"
import { xml } from "@xmpp/client"
import { isUiLanguage } from "./i18n/language"

class SettingsParser {
  static parseSettings(stanza: Element): Partial<Settings> | undefined {
    const eventOrPubsubNode =
      stanza.getChild("event", "http://jabber.org/protocol/pubsub#event") ||
      stanza.getChild("pubsub", "http://jabber.org/protocol/pubsub")

    if (!eventOrPubsubNode) return undefined

    const storageSettingsNode = eventOrPubsubNode.getChildByAttr(
      "node",
      "storage:settings",
    )

    if (!storageSettingsNode) return undefined

    const booleanSettings = {
      mobileNotificationsEnabled: storageSettingsNode
        .getChildByAttr("id", "mobile_notifications_enabled")
        ?.getChildText("value"),
      notifyOnlyOnDriverMessages: storageSettingsNode
        .getChildByAttr("id", "notify_only_on_driver_messages")
        ?.getChildText("value"),
      showNicknameInWhatsapp: storageSettingsNode
        .getChildByAttr("id", "show_nickname_in_whatsapp")
        ?.getChildText("value"),
      showTranslations: storageSettingsNode
        .getChildByAttr("id", "show_translations")
        ?.getChildText("value"),
    }

    // planner_companies to jedyne ustawienie nie-boolean w tym node'zie, więc
    // rzutowanie `value === "true"` musi je omijać. Uszkodzona wartość daje
    // pusty wybór, czyli "pokaż wszystko" — nigdy wyjątku, bo wywaliłoby to
    // cały ekran ustawień.
    const parsePlannerCompanies = (raw: string): PlannerCompany[] => {
      try {
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.filter(
          (entry): entry is PlannerCompany =>
            typeof entry?.name === "string" && entry.name.trim().length > 0,
        )
      } catch {
        return []
      }
    }

    const plannerCompaniesRaw = storageSettingsNode
      .getChildByAttr("id", "planner_companies")
      ?.getChildText("value")

    const result: Partial<Settings> = Object.fromEntries(
      Object.entries(booleanSettings)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, value === "true"]),
    )

    if (plannerCompaniesRaw != null) {
      result.plannerCompanies = parsePlannerCompanies(plannerCompaniesRaw)
    }

    // A string, not a boolean. A value this client does not support (from an
    // older or newer client) counts as "not chosen".
    const uiLanguageRaw = storageSettingsNode
      .getChildByAttr("id", "ui_language")
      ?.getChildText("value")

    if (isUiLanguage(uiLanguageRaw)) {
      result.uiLanguage = uiLanguageRaw
    }

    return result
  }

  static parsePushSubscription(commandResult: Element): PushToken | undefined {
    const subscription = commandResult
      .getChildByAttr("node", "get_subscription")
      ?.getChildText("json")

    if (!subscription) return undefined
    try {
      return JSON.parse(subscription)
    } catch {
      return undefined
    }
  }

  static parseCarRouteConfig(stanza: Element): CarRouteConfig | undefined {
    // Parse the pubsub result
    const pubsubNode = stanza.getChild(
      "pubsub",
      "http://jabber.org/protocol/pubsub",
    )
    if (!pubsubNode) return undefined

    const itemsNode = pubsubNode.getChild("items")
    if (!itemsNode) return undefined

    const itemNode = itemsNode.getChild("item")
    if (!itemNode) return undefined

    const configNode = itemNode.getChild(
      "car-route-config",
      "urn:slickshift:account:car-route-config:0",
    )
    if (!configNode) return undefined

    const config: CarRouteConfig = {}

    // Extract attributes from the config node
    if (configNode.attrs.width) config.width = configNode.attrs.width
    if (configNode.attrs.height) config.height = configNode.attrs.height
    if (configNode.attrs.length) config.length = configNode.attrs.length
    if (configNode.attrs.weight) config.weight = configNode.attrs.weight
    if (configNode.attrs["max-speed"]) {
      config.maxSpeed = configNode.attrs["max-speed"]
    }

    if (configNode.attrs.avoid) {
      config.avoid = configNode.attrs.avoid.split(",") as AvoidType[]
    }

    if (configNode.attrs["travel-mode"]) {
      config.travelMode = configNode.attrs["travel-mode"] as TravelMode
    }

    if (configNode.attrs["emission-type"]) {
      config.emissionType = configNode.attrs["emission-type"] as EmissionType
    }

    if (configNode.attrs["load-type"]) {
      config.loadType = configNode.attrs["load-type"].split(",") as LoadType[]
    }

    if (configNode.attrs["axle-count"]) {
      config.axleCount = configNode.attrs["axle-count"]
    }

    if (configNode.attrs["co2-class"]) {
      config.co2class = configNode.attrs["co2-class"]
    }

    if (configNode.attrs["fuel-consumption"]) {
      config.fuelConsumption = configNode.attrs["fuel-consumption"]
    }

    for (const field of [
      "temperature_main_alert_level_min",
      "temperature_main_alert_level_max",
      "temperature_additional_alert_level_min",
      "temperature_additional_alert_level_max",
    ]) {
      const value = configNode.attrs[field.replace(/_/g, "-")]
      if (value !== undefined) {
        config[field as keyof CarRouteConfig] = value
      }
    }

    return config
  }

  static parseRouteAlertConfig(stanza: Element): RouteAlertConfig | undefined {
    // Parse the pubsub result
    const pubsubNode = stanza.getChild(
      "pubsub",
      "http://jabber.org/protocol/pubsub",
    )
    if (!pubsubNode) return undefined

    const itemsNode = pubsubNode.getChild("items")
    if (!itemsNode) return undefined

    const itemNode = itemsNode.getChild("item")
    if (!itemNode) return undefined

    const configNode = itemNode.getChild(
      "route-alert-config",
      "urn:slickshift:account:route-alert-config:0",
    )
    if (!configNode) return undefined

    const config: RouteAlertConfig = {}

    // Extract temperature alert attributes from the config node
    for (const field of [
      "temperature_main_alert_level_min",
      "temperature_main_alert_level_max",
      "temperature_additional_alert_level_min",
      "temperature_additional_alert_level_max",
    ]) {
      const value = configNode.attrs[field.replace(/_/g, "-")]
      if (value !== undefined) {
        config[field as keyof RouteAlertConfig] = value
      }
    }

    return config
  }

  static buildCarRouteConfigElement(carRouteConfig: CarRouteConfig): Element {
    const configElement = xml(
      "car-route-config",
      "urn:slickshift:account:car-route-config:0",
    )

    // Add all attributes from carRouteConfig
    if (carRouteConfig.width) configElement.attrs.width = carRouteConfig.width
    if (carRouteConfig.height)
      configElement.attrs.height = carRouteConfig.height
    if (carRouteConfig.length)
      configElement.attrs.length = carRouteConfig.length
    if (carRouteConfig.weight)
      configElement.attrs.weight = carRouteConfig.weight
    if (carRouteConfig.maxSpeed)
      configElement.attrs["max-speed"] = carRouteConfig.maxSpeed
    if (carRouteConfig.travelMode)
      configElement.attrs["travel-mode"] = carRouteConfig.travelMode
    if (carRouteConfig.emissionType)
      configElement.attrs["emission-type"] = carRouteConfig.emissionType

    // Handle avoid options as a comma-separated string
    if (carRouteConfig.avoid && carRouteConfig.avoid.length > 0) {
      configElement.attrs.avoid = carRouteConfig.avoid.join(",")
    }

    // Handle load types as a comma-separated string
    if (carRouteConfig.loadType && carRouteConfig.loadType.length > 0) {
      configElement.attrs["load-type"] = carRouteConfig.loadType.join(",")
    }

    if (carRouteConfig.axleCount)
      configElement.attrs["axle-count"] = carRouteConfig.axleCount
    if (carRouteConfig.co2class)
      configElement.attrs["co2-class"] = carRouteConfig.co2class
    if (carRouteConfig.fuelConsumption)
      configElement.attrs["fuel-consumption"] = carRouteConfig.fuelConsumption

    return configElement
  }
}

export default SettingsParser
